import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const COLORS = ['#10b981', '#1a5f7a', '#f59e0b', '#ef4444', '#6b7280'];

function AdminDashboard() {
  const { token, user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [recentAvaliacoes, setRecentAvaliacoes] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchDashboard();
    fetchRecentAvaliacoes();
  }, [isAuthenticated, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentAvaliacoes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/avaliacoes?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentAvaliacoes(response.data.avaliacoes);
    } catch (err) {
      console.error('Erro ao carregar avaliações recentes:', err);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} logout={logout} navigate={navigate} />
        <main className="admin-content">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  const pieData = dashboard?.recomendacoes?.map(r => ({
    name: formatRecomendacao(r.recomendaria),
    value: parseInt(r.count)
  })) || [];

  const secoesData = dashboard?.mediasSecoes ? [
    { name: 'Recepção', media: parseFloat(dashboard.mediasSecoes.recepcao || 0).toFixed(1) },
    { name: 'Enfermagem', media: parseFloat(dashboard.mediasSecoes.enfermagem || 0).toFixed(1) },
    { name: 'Estrutura', media: parseFloat(dashboard.mediasSecoes.estrutura || 0).toFixed(1) },
    { name: 'Alimentação', media: parseFloat(dashboard.mediasSecoes.alimentacao || 0).toFixed(1) }
  ] : [];

  return (
    <div className="admin-layout">
      <Sidebar user={user} logout={logout} navigate={navigate} active="dashboard" />
      <main className="admin-content">
        <div className="admin-header">
          <h1>Dashboard</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Bem-vindo(a), {user?.nome}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="icon blue">📊</div>
            <h3>{dashboard?.total || 0}</h3>
            <p>Total de Avaliações</p>
          </div>
          <div className="stat-card">
            <div className="icon green">👍</div>
            <h3>{dashboard?.positivas || 0}</h3>
            <p>Avaliações Positivas</p>
          </div>
          <div className="stat-card">
            <div className="icon red">👎</div>
            <h3>{dashboard?.negativas || 0}</h3>
            <p>Avaliações Negativas</p>
          </div>
          <div className="stat-card">
            <div className="icon yellow">⭐</div>
            <h3>{dashboard?.mediaGeral || '0.0'}</h3>
            <p>Nota Média Geral</p>
          </div>
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          {/* Média por Seção */}
          <div className="card">
            <div className="card-header">
              <span className="icon">📈</span>
              <h2>Média por Seção</h2>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={secoesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 4]} />
                <Tooltip />
                <Bar dataKey="media" fill="#1a5f7a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recomendações */}
          <div className="card">
            <div className="card-header">
              <span className="icon">🎯</span>
              <h2>Recomendações</h2>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avaliações por Dia */}
        {dashboard?.avaliacoesPorDia?.length > 0 && (
          <div className="card" style={{ marginBottom: '30px' }}>
            <div className="card-header">
              <span className="icon">📅</span>
              <h2>Avaliações nos Últimos 30 Dias</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dashboard.avaliacoesPorDia.reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data" tickFormatter={(date) => new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" domain={[0, 10]} />
                <Tooltip
                  labelFormatter={(date) => new Date(date).toLocaleDateString('pt-BR')}
                  formatter={(value, name) => [value, name === 'count' ? 'Avaliações' : 'Média']}
                />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#1a5f7a" name="Avaliações" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="media" stroke="#10b981" name="Média" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Últimas Avaliações */}
        <div className="table-container">
          <div className="table-header">
            <h2>Últimas Avaliações</h2>
            <Link to="/admin/avaliacoes" className="btn btn-secondary" style={{ textDecoration: 'none' }}>
              Ver todas
            </Link>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Data</th>
                <th>Nota</th>
                <th>Status</th>
                <th>WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {recentAvaliacoes.map(av => (
                <tr key={av.id}>
                  <td>{av.nome}</td>
                  <td>{new Date(av.created_at).toLocaleDateString('pt-BR')}</td>
                  <td>{av.nota_geral}/10</td>
                  <td>
                    <span className={`badge ${av.avaliacao_positiva ? 'badge-success' : 'badge-error'}`}>
                      {av.avaliacao_positiva ? 'Positiva' : 'Negativa'}
                    </span>
                  </td>
                  <td>
                    {av.whatsapp_enviado ? (
                      <span style={{ color: 'var(--success-color)' }}>Enviado</span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
              {recentAvaliacoes.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Nenhuma avaliação encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Info Box */}
        <div className="card" style={{ marginTop: '30px', background: 'linear-gradient(135deg, rgba(26, 95, 122, 0.1), rgba(87, 197, 182, 0.1))' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ fontSize: '2rem' }}>📱</span>
            <div>
              <h3 style={{ color: 'var(--primary-color)', marginBottom: '5px' }}>
                {dashboard?.whatsappEnviados || 0} mensagens enviadas via WhatsApp
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Mensagens automáticas com link do Google enviadas para avaliações positivas
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Sidebar({ user, logout, navigate, active }) {
  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <aside className="admin-sidebar">
      <div className="logo">
        <span style={{ fontSize: '2.5rem' }}>🏥</span>
        <h2>Hospital Unique</h2>
        <p style={{ fontSize: '0.75rem', opacity: 0.7 }}>Portal de Avaliação</p>
      </div>
      <nav>
        <Link to="/admin/dashboard" className={active === 'dashboard' ? 'active' : ''}>
          <span>📊</span> Dashboard
        </Link>
        <Link to="/admin/avaliacoes" className={active === 'avaliacoes' ? 'active' : ''}>
          <span>📝</span> Avaliações
        </Link>
        <a href="/" target="_blank" rel="noopener noreferrer">
          <span>🔗</span> Ver Formulário
        </a>
      </nav>
      <div style={{ marginTop: 'auto', paddingTop: '30px' }}>
        <div style={{ padding: '15px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', marginBottom: '15px' }}>
          <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>Logado como</p>
          <p style={{ fontWeight: '600' }}>{user?.nome}</p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '12px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span>🚪</span> Sair
        </button>
      </div>
    </aside>
  );
}

function formatRecomendacao(value) {
  const map = {
    'sim_certeza': 'Sim, certeza',
    'provavelmente_sim': 'Provavelmente',
    'talvez': 'Talvez',
    'provavelmente_nao': 'Prob. não',
    'nao': 'Não'
  };
  return map[value] || value;
}

export { Sidebar };
export default AdminDashboard;
